import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fmtAED } from '../utils/money';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Link2, Copy, Check, Loader2, Send, Trash2, 
  CheckCircle, Clock, XCircle, User, Mail, Phone, FileText,
  RefreshCw, ExternalLink, Search, UserCheck, UserPlus, ChevronDown,
  MessageSquare, MailCheck, Smartphone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { DirhamPrice } from '../components/DirhamPrice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// أوصاف الدفع الرئيسية
const PAYMENT_DESCRIPTIONS = {
  cloudgm: [
    { value: 'visa_service', label_ar: 'خدمة تأشيرة', label_en: 'Visa Service' },
    { value: 'emirates_id', label_ar: 'هوية إماراتية', label_en: 'Emirates ID' },
    { value: 'medical_exam', label_ar: 'فحص طبي', label_en: 'Medical Exam' },
    { value: 'document_typing', label_ar: 'طباعة مستندات', label_en: 'Document Typing' },
    { value: 'consultation', label_ar: 'استشارة', label_en: 'Consultation' },
    { value: 'renewal', label_ar: 'تجديد', label_en: 'Renewal' },
    { value: 'other', label_ar: 'أخرى', label_en: 'Other' }
  ],
  cloudgm_business: [
    { value: 'subscription', label_ar: 'اشتراك', label_en: 'Subscription' },
    { value: 'license_renewal', label_ar: 'تجديد رخصة', label_en: 'License Renewal' },
    { value: 'setup_fee', label_ar: 'رسوم تأسيس', label_en: 'Setup Fee' },
    { value: 'service_fee', label_ar: 'رسوم خدمة', label_en: 'Service Fee' },
    { value: 'advertisement', label_ar: 'إعلان', label_en: 'Advertisement' },
    { value: 'other', label_ar: 'أخرى', label_en: 'Other' }
  ]
};

// فتح خطوط الهاتف
const PHONE_CODES = [
  { code: '+971', country: 'UAE', flag: '🇦🇪' }
];

export default function PaymentLinks() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const platform = searchParams.get('platform') || 'cloudgm';
  const fromFinance = searchParams.get('from') === 'finance';
  const { language } = useLanguage();
  const { token } = useAuth();
  
  // State
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Members State
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  
  // Form State
  const [memberType, setMemberType] = useState('registered'); // 'registered' | 'unregistered'
  const [selectedMember, setSelectedMember] = useState(null);
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'AED',
    customer_name: '',
    customer_email: '',
    phone_code: '+971',
    customer_phone: '',
    description_type: '',
    description_custom: ''
  });
  
  // Action Options
  const [actionOptions, setActionOptions] = useState({
    createLink: true,
    sendEmail: false,
    sendSMS: false
  });

  const getHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  // Fetch transactions
  const fetchTransactions = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/payments/transactions?platform=${platform}`,
        { headers: getHeaders() }
      );
      setTransactions(response.data.data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch members based on platform
  const fetchMembers = useCallback(async (search = '') => {
    setLoadingMembers(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/users?platform=${platform}&search=${search}`,
        { headers: getHeaders() }
      );
      setMembers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoadingMembers(false);
    }
  }, [platform, token]);

  useEffect(() => {
    fetchTransactions();
    fetchMembers();
  }, [platform]);

  // Search members with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (memberSearchQuery.length >= 2) {
        fetchMembers(memberSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearchQuery, fetchMembers]);

  // Select member
  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setFormData(prev => ({
      ...prev,
      customer_name: member.name || member.full_name || `${member.first_name || ''} ${member.last_name || ''}`.trim(),
      customer_email: member.email || '',
      customer_phone: member.phone?.replace(/^\+971/, '') || ''
    }));
    setShowMemberDropdown(false);
    setMemberSearchQuery('');
  };

  // Validate email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Reset form
  const resetForm = () => {
    setMemberType('registered');
    setSelectedMember(null);
    setFormData({
      amount: '',
      currency: 'AED',
      customer_name: '',
      customer_email: '',
      phone_code: '+971',
      customer_phone: '',
      description_type: '',
      description_custom: ''
    });
    setActionOptions({
      createLink: true,
      sendEmail: false,
      sendSMS: false
    });
    setMemberSearchQuery('');
  };

  // Create payment link
  const handleCreateLink = async () => {
    // Validation
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error(language === 'ar' ? 'يرجى إدخال مبلغ صحيح' : 'Please enter a valid amount');
      return;
    }
    
    if (!formData.customer_name.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال اسم العميل' : 'Please enter customer name');
      return;
    }
    
    if (formData.customer_email && !isValidEmail(formData.customer_email)) {
      toast.error(language === 'ar' ? 'يرجى إدخال بريد إلكتروني صحيح' : 'Please enter a valid email');
      return;
    }
    
    if (!formData.description_type) {
      toast.error(language === 'ar' ? 'يرجى اختيار وصف الدفعة' : 'Please select payment description');
      return;
    }
    
    if (formData.description_type === 'other' && !formData.description_custom.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال وصف الدفعة' : 'Please enter payment description');
      return;
    }

    if (!actionOptions.createLink && !actionOptions.sendEmail && !actionOptions.sendSMS) {
      toast.error(language === 'ar' ? 'يرجى اختيار طريقة واحدة على الأقل' : 'Please select at least one action');
      return;
    }

    if ((actionOptions.sendEmail) && !formData.customer_email) {
      toast.error(language === 'ar' ? 'يرجى إدخال البريد الإلكتروني للإرسال' : 'Please enter email to send');
      return;
    }

    if (actionOptions.sendSMS && !formData.customer_phone) {
      toast.error(language === 'ar' ? 'يرجى إدخال رقم الهاتف للإرسال' : 'Please enter phone to send SMS');
      return;
    }

    if (!token) {
      toast.error(language === 'ar' ? 'يرجى تسجيل الدخول مرة أخرى' : 'Please login again');
      return;
    }

    setCreating(true);
    try {
      // Build description
      const descriptionLabel = PAYMENT_DESCRIPTIONS[platform]?.find(d => d.value === formData.description_type);
      const primaryDescription = descriptionLabel 
        ? (language === 'ar' ? descriptionLabel.label_ar : descriptionLabel.label_en)
        : formData.description_type;
      const fullDescription = formData.description_custom 
        ? `${primaryDescription} - ${formData.description_custom}`
        : primaryDescription;

      // Build phone number
      const fullPhone = formData.customer_phone 
        ? `${formData.phone_code}${formData.customer_phone.replace(/^0/, '')}`
        : null;

      const response = await axios.post(
        `${BACKEND_URL}/api/payments/create-link`,
        {
          amount: parseFloat(formData.amount),
          currency: formData.currency.toLowerCase(),
          customer_name: formData.customer_name,
          customer_email: formData.customer_email || null,
          customer_phone: fullPhone,
          description: fullDescription,
          platform: platform,
          origin_url: window.location.origin,
          member_id: selectedMember?.id || null,
          member_type: memberType,
          actions: actionOptions
        },
        { headers: getHeaders() }
      );

      if (response.data.success) {
        // Handle different actions
        if (actionOptions.createLink) {
          try {
            await navigator.clipboard.writeText(response.data.payment_link);
            toast.success(language === 'ar' ? 'تم إنشاء ونسخ رابط الدفع!' : 'Payment link created and copied!');
          } catch (e) {
            toast.success(language === 'ar' ? 'تم إنشاء رابط الدفع بنجاح' : 'Payment link created successfully');
          }
        }
        
        if (actionOptions.sendEmail && response.data.email_sent) {
          toast.success(language === 'ar' ? 'تم إرسال الرابط عبر البريد الإلكتروني' : 'Link sent via email');
        }
        
        if (actionOptions.sendSMS && response.data.sms_sent) {
          toast.success(language === 'ar' ? 'تم إرسال الرابط عبر رسالة نصية' : 'Link sent via SMS');
        }
        
        setShowCreateModal(false);
        resetForm();
        fetchTransactions();
      } else {
        toast.error(response.data.message || 'فشل في إنشاء رابط الدفع');
      }
    } catch (error) {
      console.error('Create link error:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'فشل في إنشاء رابط الدفع';
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  // Copy link
  const copyToClipboard = async (link, id) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(id);
      toast.success(language === 'ar' ? 'تم نسخ الرابط!' : 'Link copied!');
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (error) {
      toast.error('فشل في نسخ الرابط');
    }
  };

  // Delete transaction
  const handleDelete = async (transactionId) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذه المعاملة؟' : 'Are you sure you want to delete this transaction?')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/payments/transactions/${transactionId}`,
        { headers: getHeaders() }
      );
      toast.success(language === 'ar' ? 'تم حذف المعاملة' : 'Transaction deleted');
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل في حذف المعاملة');
    }
  };

  // Check payment status
  const checkStatus = async (sessionId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/payments/status/${sessionId}`,
        { headers: getHeaders() }
      );
      
      if (response.data.payment_status === 'paid') {
        toast.success(language === 'ar' ? 'تم الدفع بنجاح!' : 'Payment successful!');
      } else {
        toast.info(language === 'ar' ? `حالة الدفع: ${response.data.payment_status}` : `Payment status: ${response.data.payment_status}`);
      }
      
      fetchTransactions();
    } catch (error) {
      toast.error('فشل في التحقق من الحالة');
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      'paid': { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/20', label: language === 'ar' ? 'مدفوع' : 'Paid' },
      'pending': { icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/20', label: language === 'ar' ? 'معلق' : 'Pending' },
      'initiated': { icon: Clock, color: 'text-blue-400', bg: 'bg-blue-500/20', label: language === 'ar' ? 'جاري' : 'Initiated' },
      'expired': { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/20', label: language === 'ar' ? 'منتهي' : 'Expired' },
      'cancelled': { icon: XCircle, color: 'text-gray-400', bg: 'bg-gray-500/20', label: language === 'ar' ? 'ملغي' : 'Cancelled' }
    };
    
    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
        <Icon className="h-3.5 w-3.5" />
        {config.label}
      </span>
    );
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(t => {
    if (statusFilter === 'all') return true;
    return t.payment_status === statusFilter;
  });

  const platformName = platform === 'cloudgm' 
    ? (language === 'ar' ? 'منصة العملاء' : 'Customer Platform') 
    : (language === 'ar' ? 'منصة التجار' : 'Merchant Platform');
  
  const memberLabel = platform === 'cloudgm'
    ? (language === 'ar' ? 'عميل' : 'Customer')
    : (language === 'ar' ? 'تاجر' : 'Merchant');

  const descriptions = PAYMENT_DESCRIPTIONS[platform] || PAYMENT_DESCRIPTIONS.cloudgm;

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      {/* Header */}
      <DashboardHeader
        title={language === 'ar' ? 'روابط الدفع' : 'Payment Links'}
        subtitle={platformName}
        icon={Link2}
        iconColor="#d4af37"
        onBack={() => {
          if (fromFinance) {
            navigate(platform === 'cloudgm' 
              ? '/dashboard/finance/cloudgm/customers/payments'
              : '/dashboard/finance/cloudgm/merchants/payments'
            );
          } else {
            navigate(`/manage/${platform === 'cloudgm' ? 'cloudgm' : 'cloudgm-business'}`);
          }
        }}
      />

      {/* Main Content */}
      <main className="relative z-10">
        {/* Breadcrumb with border */}
        <div className="border-b border-white/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <BreadcrumbNav items={fromFinance ? [
              { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
              { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
              { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard/finance/cloudgm' },
              { label_ar: platform === 'cloudgm' ? 'منصة العملاء (المالية)' : 'منصة التجار (المالية)', label_en: platform === 'cloudgm' ? 'Customer Platform (Finance)' : 'Merchant Platform (Finance)', path: platform === 'cloudgm' ? '/dashboard/finance/cloudgm/customers' : '/dashboard/finance/cloudgm/merchants' },
              { label_ar: 'المدفوعات', label_en: 'Payments', path: platform === 'cloudgm' ? '/dashboard/finance/cloudgm/customers/payments' : '/dashboard/finance/cloudgm/merchants/payments' },
              { label_ar: 'إنشاء روابط دفع', label_en: 'Payment Links', path: null }
            ] : [
              { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
              { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard?view=cloudgm' },
              { label_ar: platform === 'cloudgm' ? 'منصة العملاء' : 'منصة التجار', label_en: platform === 'cloudgm' ? 'Customers Platform' : 'Merchants Platform', path: `/dashboard?view=cloudgm&platform=${platform === 'cloudgm' ? 'customers' : 'merchants'}` },
              { label_ar: 'الإشراف على المنصة', label_en: 'Platform Supervision', path: `/manage/${platform === 'cloudgm' ? 'cloudgm' : 'cloudgm-business'}` },
              { label_ar: 'روابط الدفع', label_en: 'Payment Links', path: null }
            ]} />
          </div>
        </div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

          {/* Actions Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Link2 className="h-6 w-6 text-[#d4af37]" />
              {language === 'ar' ? 'روابط الدفع' : 'Payment Links'}
              <span className="text-sm font-normal text-[#71717A]">({filteredTransactions.length})</span>
            </h2>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-[#1a1a2e] border border-white/10 rounded-xl text-white text-sm focus:border-[#d4af37] focus:outline-none cursor-pointer [&>option]:bg-[#1a1a2e] [&>option]:text-white"
              >
                <option value="all">{language === 'ar' ? 'كل الحالات' : 'All Status'}</option>
                <option value="paid">{language === 'ar' ? 'مدفوع' : 'Paid'}</option>
                <option value="pending">{language === 'ar' ? 'معلق' : 'Pending'}</option>
                <option value="initiated">{language === 'ar' ? 'جاري' : 'Initiated'}</option>
                <option value="expired">{language === 'ar' ? 'منتهي' : 'Expired'}</option>
              </select>

              {/* Refresh */}
              <Button
                variant="outline"
                onClick={fetchTransactions}
                className="border-white/10 text-white hover:bg-white/5"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>

              {/* Create New */}
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-r from-[#d4af37] to-[#c9a227] hover:from-[#c9a227] hover:to-[#b8922a] text-black font-medium"
              >
                <Link2 className="h-4 w-4 me-2" />
                {language === 'ar' ? 'إنشاء رابط دفع' : 'Create Payment Link'}
              </Button>
            </div>
          </div>

          {/* Transactions List */}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-24 bg-white/[0.02] rounded-xl animate-pulse" />
              ))}
            </div>
          ) : filteredTransactions.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16 bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10"
            >
              <Link2 className="h-16 w-16 mx-auto mb-4 opacity-30 text-[#d4af37]" />
              <h3 className="text-lg font-medium text-white mb-2">
                {language === 'ar' ? 'لا توجد روابط دفع' : 'No payment links'}
              </h3>
              <p className="text-[#71717A] mb-4">
                {language === 'ar' ? 'أنشئ رابط دفع جديد للبدء' : 'Create a new payment link to get started'}
              </p>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-[#d4af37] hover:bg-[#c9a227] text-black"
              >
                <Link2 className="h-4 w-4 me-2" />
                {language === 'ar' ? 'إنشاء رابط دفع' : 'Create Payment Link'}
              </Button>
            </motion.div>
          ) : (
            <div className="space-y-3">
              <AnimatePresence>
                {filteredTransactions.map((transaction, index) => (
                  <motion.div
                    key={transaction.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-5 border border-white/10 hover:border-[#d4af37]/30 transition-all"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      {/* Info */}
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3">
                          <h3 className="text-white font-medium">{transaction.customer_name}</h3>
                          {getStatusBadge(transaction.payment_status)}
                        </div>
                        
                        <p className="text-[#71717A] text-sm">{transaction.description}</p>
                        
                        <div className="flex flex-wrap items-center gap-4 text-xs text-[#71717A]">
                          {transaction.customer_email && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3.5 w-3.5" />
                              {transaction.customer_email}
                            </span>
                          )}
                          {transaction.customer_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3.5 w-3.5" />
                              {transaction.customer_phone}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {new Date(transaction.created_at).toLocaleDateString('ar-AE')}
                          </span>
                        </div>
                      </div>

                      {/* Amount */}
                      <div className="text-center lg:text-end">
                        <p className="text-2xl font-bold text-[#d4af37]">
                          {fmtAED(transaction.amount)} <span className="text-sm">{transaction.currency}</span>
                        </p>
                        {transaction.paid_at && (
                          <p className="text-xs text-emerald-400 mt-1">
                            {language === 'ar' ? 'تم الدفع:' : 'Paid:'} {new Date(transaction.paid_at).toLocaleDateString('ar-AE')}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {transaction.payment_status !== 'paid' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyToClipboard(transaction.payment_link, transaction.id)}
                              className="border-white/10 text-white hover:bg-white/5"
                              title={language === 'ar' ? 'نسخ الرابط' : 'Copy Link'}
                            >
                              {copiedLink === transaction.id ? (
                                <Check className="h-4 w-4 text-emerald-400" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </Button>
                            
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(transaction.payment_link, '_blank')}
                              className="border-white/10 text-white hover:bg-white/5"
                              title={language === 'ar' ? 'فتح الرابط' : 'Open Link'}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>

                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => checkStatus(transaction.session_id)}
                              className="border-white/10 text-white hover:bg-white/5"
                              title={language === 'ar' ? 'تحديث الحالة' : 'Refresh Status'}
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          </>
                        )}

                        {transaction.payment_status !== 'paid' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(transaction.id)}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            title={language === 'ar' ? 'حذف' : 'Delete'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </main>

      {/* Create Payment Link Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        setShowCreateModal(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#d4af37] flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              {language === 'ar' ? 'إنشاء رابط دفع جديد' : 'Create New Payment Link'}
            </DialogTitle>
            <DialogDescription className="text-[#71717A]">
              {platformName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            
            {/* Member Type Selection */}
            <div>
              <label className="block text-sm font-medium text-[#71717A] mb-3">
                {language === 'ar' ? `نوع ${memberLabel}` : `${memberLabel} Type`}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setMemberType('registered');
                    setSelectedMember(null);
                    setFormData(prev => ({ ...prev, customer_name: '', customer_email: '', customer_phone: '' }));
                  }}
                  className={`p-3 rounded-xl border ${memberType === 'registered' 
                    ? 'border-[#d4af37] bg-[#d4af37]/10' 
                    : 'border-white/10 hover:border-white/20'} transition-all flex flex-col items-center gap-2`}
                >
                  <UserCheck className={`h-5 w-5 ${memberType === 'registered' ? 'text-[#d4af37]' : 'text-white/60'}`} />
                  <span className={`text-sm ${memberType === 'registered' ? 'text-[#d4af37]' : 'text-white/60'}`}>
                    {language === 'ar' ? `${memberLabel} مسجل` : `Registered ${memberLabel}`}
                  </span>
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    setMemberType('unregistered');
                    setSelectedMember(null);
                    setFormData(prev => ({ ...prev, customer_name: '', customer_email: '', customer_phone: '' }));
                  }}
                  className={`p-3 rounded-xl border ${memberType === 'unregistered' 
                    ? 'border-[#d4af37] bg-[#d4af37]/10' 
                    : 'border-white/10 hover:border-white/20'} transition-all flex flex-col items-center gap-2`}
                >
                  <UserPlus className={`h-5 w-5 ${memberType === 'unregistered' ? 'text-[#d4af37]' : 'text-white/60'}`} />
                  <span className={`text-sm ${memberType === 'unregistered' ? 'text-[#d4af37]' : 'text-white/60'}`}>
                    {language === 'ar' ? `${memberLabel} غير مسجل` : `Unregistered ${memberLabel}`}
                  </span>
                </button>
              </div>
            </div>

            {/* Member Search (for registered) */}
            {memberType === 'registered' && (
              <div className="relative">
                <label className="block text-sm font-medium text-[#71717A] mb-2">
                  {language === 'ar' ? `البحث عن ${memberLabel}` : `Search ${memberLabel}`}
                </label>
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" />
                  <Input
                    value={memberSearchQuery}
                    onChange={(e) => {
                      setMemberSearchQuery(e.target.value);
                      setShowMemberDropdown(true);
                    }}
                    onFocus={() => setShowMemberDropdown(true)}
                    placeholder={language === 'ar' ? 'ابحث بالاسم أو البريد الإلكتروني...' : 'Search by name or email...'}
                    className="bg-white/5 border-white/10 text-white focus:border-[#d4af37] pr-10"
                  />
                </div>
                
                {/* Members Dropdown */}
                {showMemberDropdown && members.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-[#1a1a2e] border border-white/10 rounded-xl max-h-48 overflow-y-auto">
                    {loadingMembers ? (
                      <div className="p-3 text-center text-[#71717A]">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                      </div>
                    ) : (
                      members.slice(0, 10).map(member => (
                        <button
                          key={member.id}
                          type="button"
                          onClick={() => handleSelectMember(member)}
                          className="w-full p-3 text-start hover:bg-white/5 flex items-center gap-3 border-b border-white/5 last:border-0"
                        >
                          <div className="w-8 h-8 rounded-full bg-[#d4af37]/20 flex items-center justify-center">
                            <User className="h-4 w-4 text-[#d4af37]" />
                          </div>
                          <div>
                            <p className="text-white text-sm">{member.name || member.full_name || `${member.first_name} ${member.last_name}`}</p>
                            <p className="text-[#71717A] text-xs">{member.email}</p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}

                {/* Selected Member */}
                {selectedMember && (
                  <div className="mt-2 p-3 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserCheck className="h-4 w-4 text-[#d4af37]" />
                      <span className="text-white text-sm">{selectedMember.name || selectedMember.full_name}</span>
                      <span className="text-[#71717A] text-xs">({selectedMember.email})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedMember(null);
                        setFormData(prev => ({ ...prev, customer_name: '', customer_email: '', customer_phone: '' }));
                      }}
                      className="text-[#71717A] hover:text-white"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Customer Name (for unregistered or show selected) */}
            {(memberType === 'unregistered' || selectedMember) && (
              <div>
                <label className="block text-sm font-medium text-[#71717A] mb-2">
                  {language === 'ar' ? `اسم ${memberLabel}` : `${memberLabel} Name`} *
                </label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  placeholder={language === 'ar' ? 'أدخل الاسم الكامل' : 'Enter full name'}
                  className="bg-white/5 border-white/10 text-white focus:border-[#d4af37]"
                  disabled={memberType === 'registered' && selectedMember}
                />
              </div>
            )}

            {/* Email */}
            {(memberType === 'unregistered' || selectedMember) && (
              <div>
                <label className="block text-sm font-medium text-[#71717A] mb-2">
                  {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                </label>
                <Input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  placeholder="example@email.com"
                  className={`bg-white/5 border-white/10 text-white focus:border-[#d4af37] ${
                    formData.customer_email && !isValidEmail(formData.customer_email) ? 'border-red-500' : ''
                  }`}
                  dir="ltr"
                  disabled={memberType === 'registered' && selectedMember}
                />
                {formData.customer_email && !isValidEmail(formData.customer_email) && (
                  <p className="text-red-400 text-xs mt-1">{language === 'ar' ? 'بريد إلكتروني غير صالح' : 'Invalid email'}</p>
                )}
              </div>
            )}

            {/* Phone */}
            {(memberType === 'unregistered' || selectedMember) && (
              <div>
                <label className="block text-sm font-medium text-[#71717A] mb-2">
                  {language === 'ar' ? 'رقم الهاتف (اختياري)' : 'Phone (Optional)'}
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.phone_code}
                    onChange={(e) => setFormData({ ...formData, phone_code: e.target.value })}
                    className="w-28 px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:border-[#d4af37] focus:outline-none [&>option]:bg-[#1a1a2e] [&>option]:text-white"
                  >
                    {PHONE_CODES.map(pc => (
                      <option key={pc.code} value={pc.code}>{pc.flag} {pc.code}</option>
                    ))}
                  </select>
                  <Input
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value.replace(/\D/g, '') })}
                    placeholder="50 123 4567"
                    className="flex-1 bg-white/5 border-white/10 text-white focus:border-[#d4af37]"
                    dir="ltr"
                  />
                </div>
              </div>
            )}

            {/* Amount & Currency */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-[#71717A] mb-2">
                  {language === 'ar' ? 'المبلغ' : 'Amount'} *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="1"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="100.00"
                  className="bg-white/5 border-white/10 text-white focus:border-[#d4af37]"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#71717A] mb-2">
                  {language === 'ar' ? 'العملة' : 'Currency'}
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full h-10 px-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-[#d4af37] focus:outline-none [&>option]:bg-[#1a1a2e] [&>option]:text-white"
                >
                  <option value="AED">AED</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>

            {/* Description Type */}
            <div>
              <label className="block text-sm font-medium text-[#71717A] mb-2">
                {language === 'ar' ? 'وصف الدفعة' : 'Payment Description'} *
              </label>
              <select
                value={formData.description_type}
                onChange={(e) => setFormData({ ...formData, description_type: e.target.value })}
                className="w-full h-10 px-3 bg-white/5 border border-white/10 rounded-md text-white focus:border-[#d4af37] focus:outline-none [&>option]:bg-[#1a1a2e] [&>option]:text-white"
              >
                <option value="">{language === 'ar' ? '-- اختر وصف الدفعة --' : '-- Select Description --'}</option>
                {descriptions.map(desc => (
                  <option key={desc.value} value={desc.value}>
                    {language === 'ar' ? desc.label_ar : desc.label_en}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom Description (always visible, but required if "other" selected) */}
            <div>
              <label className="block text-sm font-medium text-[#71717A] mb-2">
                {language === 'ar' ? 'وصف إضافي' : 'Additional Description'} {formData.description_type === 'other' && '*'}
              </label>
              <Input
                value={formData.description_custom}
                onChange={(e) => setFormData({ ...formData, description_custom: e.target.value })}
                placeholder={language === 'ar' ? 'تفاصيل إضافية...' : 'Additional details...'}
                className="bg-white/5 border-white/10 text-white focus:border-[#d4af37]"
              />
            </div>

            {/* Action Options */}
            <div>
              <label className="block text-sm font-medium text-[#71717A] mb-3">
                {language === 'ar' ? 'خيارات الإرسال' : 'Send Options'}
              </label>
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 bg-white/[0.02] rounded-xl border border-white/10 cursor-pointer hover:border-white/20">
                  <input
                    type="checkbox"
                    checked={actionOptions.createLink}
                    onChange={(e) => setActionOptions({ ...actionOptions, createLink: e.target.checked })}
                    className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#d4af37] focus:ring-[#d4af37]"
                  />
                  <Link2 className="h-4 w-4 text-[#d4af37]" />
                  <span className="text-white text-sm">{language === 'ar' ? 'إنشاء رابط ونسخه' : 'Create & Copy Link'}</span>
                </label>
                
                <label className={`flex items-center gap-3 p-3 bg-white/[0.02] rounded-xl border border-white/10 cursor-pointer hover:border-white/20 ${!formData.customer_email ? 'opacity-50' : ''}`}>
                  <input
                    type="checkbox"
                    checked={actionOptions.sendEmail}
                    onChange={(e) => setActionOptions({ ...actionOptions, sendEmail: e.target.checked })}
                    disabled={!formData.customer_email}
                    className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#d4af37] focus:ring-[#d4af37]"
                  />
                  <MailCheck className="h-4 w-4 text-blue-400" />
                  <span className="text-white text-sm">{language === 'ar' ? 'إرسال عبر البريد الإلكتروني' : 'Send via Email'}</span>
                </label>
                
                <label className={`flex items-center gap-3 p-3 bg-white/[0.02] rounded-xl border border-white/10 cursor-pointer hover:border-white/20 ${!formData.customer_phone ? 'opacity-50' : ''}`}>
                  <input
                    type="checkbox"
                    checked={actionOptions.sendSMS}
                    onChange={(e) => setActionOptions({ ...actionOptions, sendSMS: e.target.checked })}
                    disabled={!formData.customer_phone}
                    className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#d4af37] focus:ring-[#d4af37]"
                  />
                  <Smartphone className="h-4 w-4 text-emerald-400" />
                  <span className="text-white text-sm">{language === 'ar' ? 'إرسال رسالة نصية SMS' : 'Send SMS'}</span>
                </label>
              </div>
            </div>

          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
              className="border-white/10 text-white hover:bg-white/5"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleCreateLink}
              disabled={creating}
              className="bg-[#d4af37] hover:bg-[#c9a227] text-black"
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin me-2" />
                  {language === 'ar' ? 'جاري الإنشاء...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Link2 className="h-4 w-4 me-2" />
                  {language === 'ar' ? 'إنشاء الرابط' : 'Create Link'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
